import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Building2, MapPin } from "lucide-react";
import { orgsApi } from "@/api/client";
import { useAppData } from "@/context/AppDataContext";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { OrgFormDialog } from "@/components/forms/OrgFormDialog";
import { inputCls } from "@/components/FormKit";

export default function Leads() {
  const navigate = useNavigate();
  const { enums } = useAppData();
  const { canManageLeads, user, isManager } = useAuth();
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [orgType, setOrgType] = useState("");
  const [venueType, setVenueType] = useState("");
  const [assigned, setAssigned] = useState(isManager ? "all" : "mine");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await orgsApi.list({
        q: q || undefined,
        status: status || undefined,
        organisation_type: orgType || undefined,
        venue_type: venueType || undefined,
        assigned: assigned || undefined,
        page_size: 100,
      });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q, status, orgType, venueType, assigned]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setShowNew(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:py-8">
      <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" data-testid="leads-segmented">
        <button data-testid="seg-my-leads" className="rounded-lg bg-white py-2 text-sm font-semibold text-teal-700 shadow-sm">My Leads</button>
        <button data-testid="seg-discover" onClick={() => navigate("/discover")} className="rounded-lg py-2 text-sm font-semibold text-slate-500">Prospect Discovery</button>
      </div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
          Leads
        </h1>
        <span className="text-xs font-medium text-slate-400" data-testid="leads-count">
          {total} organisation{total === 1 ? "" : "s"}
        </span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="leads-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search organisations…"
          className={`${inputCls} pl-9`}
        />
      </div>

      {/* Assignment filter chips */}
      <div className="mt-3 flex gap-2" data-testid="leads-assigned-chips">
        {[["mine", "Assigned to me"], ["unclaimed", "Unclaimed"], ...(isManager ? [["all", "All"]] : [])].map(([val, label]) => (
          <button key={val} data-testid={`leads-chip-${val}`} onClick={() => setAssigned(val)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${assigned === val ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <select data-testid="filter-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !min-h-[44px] px-2 text-sm`}>
          <option value="">All statuses</option>
          {(enums?.org_statuses || []).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="filter-org-type-select" value={orgType} onChange={(e) => setOrgType(e.target.value)} className={`${inputCls} !min-h-[44px] px-2 text-sm`}>
          <option value="">All types</option>
          {(enums?.organisation_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="filter-venue-type-select" value={venueType} onChange={(e) => setVenueType(e.target.value)} className={`${inputCls} !min-h-[44px] px-2 text-sm`}>
          <option value="">All venues</option>
          {(enums?.venue_types || []).map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="mt-4 space-y-2.5">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl border border-slate-200 bg-white" />
          ))
        ) : items.length === 0 ? (
          <EmptyState
            testid="leads-empty-state"
            icon={Building2}
            title="No organisations yet"
            description="Add your first hospitality lead to start building your patch."
            action={
              canManageLeads && (
                <button data-testid="leads-empty-add" onClick={() => setShowNew(true)} className="min-h-[48px] rounded-lg bg-teal-700 px-5 font-semibold text-white hover:bg-teal-800">
                  Add organisation
                </button>
              )
            }
          />
        ) : (
          items.map((o) => (
            <button
              key={o.id}
              data-testid={`lead-card-${o.id}`}
              onClick={() => navigate(`/leads/${o.id}`)}
              className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <Building2 className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-heading text-sm font-bold text-slate-800">
                  {o.legal_name}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                  {o.organisation_type && <span className="capitalize">{o.organisation_type.replace(/_/g, " ")}</span>}
                  {o.website && <MapPin className="h-3 w-3" />}
                </span>
              </span>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {!o.assigned_user_id ? (
                  <span data-testid={`unclaimed-badge-${o.id}`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                    Unclaimed
                  </span>
                ) : (
                  <span data-testid={`assignee-${o.id}`} className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100 text-[10px] font-bold text-teal-800" title="Assigned">
                    {o.assigned_user_id === user?.id ? "You" : "•"}
                  </span>
                )}
                {o.suppression?.active && (
                  <span data-testid={`suppressed-badge-${o.id}`} className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                    Suppressed
                  </span>
                )}
                <StatusBadge status={o.status} />
              </div>
            </button>
          ))
        )}
      </div>

      {/* One-handed FAB */}
      {canManageLeads && (
        <button
          data-testid="add-lead-fab"
          onClick={() => setShowNew(true)}
          className="fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-teal-700 text-white shadow-lg transition-transform hover:scale-105 hover:bg-teal-800 md:bottom-8 md:right-8"
          aria-label="Add organisation"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      <OrgFormDialog open={showNew} onOpenChange={setShowNew} onSaved={(o) => navigate(`/leads/${o.id}`)} />
    </div>
  );
}
